<!--
  Ask ELSA — order analysis (AB#21202).

  A TAB, not an overlay and not a button. The order detail is already the
  right-hand pane, so a panel sliding in from the right would cover the
  timeline and the comments a CSM needs open while reading a risk claim — and
  every claim here cites one of them. A button in the action row would be a
  second control for one destination, which is what AB#21204 just finished
  removing twelve of.

  Two cards, and they are not interchangeable:

    - "Time in current stage" is MEASURED. Two timestamps and a percentile over
      past orders. It renders when ELSA is unavailable, unkeyed or wrong, and it
      is what the card below can be checked against.
    - "ELSA's read" is GENERATED and says so. Every claim carries the event it
      came from, because an unverifiable claim is indistinguishable on screen
      from a verifiable one and the first wrong one costs the feature its
      audience.

  There is no confidence percentage. See useOrderAnalysis and AB#21202's
  comments for why coverage replaced it.
-->
<template>
  <div class="ask-elsa">
    <!-- MEASURED -->
    <section class="elsa-card" data-test="elsa-measured">
      <header class="elsa-card-head">
        <h3 class="elsa-card-title">Time in current stage</h3>
        <span class="elsa-card-note">measured · from this order's timeline</span>
      </header>

      <div v-if="isLoading && !measured" class="elsa-skeleton" aria-hidden="true"></div>

      <template v-else-if="measured">
        <p class="elsa-metric">
          <span
            class="elsa-metric-value"
            :class="`elsa-metric-value--${measured.verdict ?? 'none'}`"
          >
            {{ measured.days_in_stage }}
          </span>
          <span class="elsa-metric-unit">
            {{ measured.days_in_stage === 1 ? 'day' : 'days' }} in {{ measured.stage_label }}
            <!--
              The gap, in the headline, so the band confirms a number that has
              already been read rather than being the only place it exists.
            -->
            <template v-if="gapText">· {{ gapText }}</template>
          </span>
        </p>

        <div v-if="measured.has_comparison" class="elsa-gauge">
          <!--
            The value flag gets a row to itself. It used to sit in the same
            strip as the zone names, and at the widths this pane actually takes
            the two collided.
          -->
          <div class="elsa-gauge-flagrow">
            <span class="elsa-gauge-flag" :style="{ left: markerLeft }">
              {{ measured.days_in_stage }}d
            </span>
          </div>

          <!--
            One linear scale, and the segments really are on it.

            These carried flex-grow without a flex-basis, so each one first
            claimed the width of its own label and only the LEFTOVER was shared
            by the grow factors. Every boundary sat to the right of the tick
            that named it, while the marker was placed on the true scale — so a
            reading genuinely inside Watch was drawn on the edge of Typical.
            flex-basis: 0 is the fix; the labels moved to the key below, which
            is what made the bug possible in the first place.
          -->
          <div class="elsa-gauge-rail" role="img" :aria-label="bandDescription">
            <div
              class="elsa-gauge-zone elsa-gauge-zone--good"
              :style="{ flexGrow: segments.good }"
            ></div>
            <div
              class="elsa-gauge-zone elsa-gauge-zone--watch"
              :class="{ 'elsa-gauge-zone--on': measured.verdict === 'watch' }"
              :style="{ flexGrow: segments.watch }"
            ></div>
            <div
              class="elsa-gauge-zone elsa-gauge-zone--late"
              :class="{ 'elsa-gauge-zone--on': measured.verdict === 'late' }"
              :style="{ flexGrow: segments.late }"
            ></div>

            <!--
              The promise on the same axis. Only drawn when the backend could
              place it: a mark past the right-hand edge piles up against the end
              and reads as "due now", which is the opposite of a distant date.
            -->
            <span
              v-if="promiseLeft"
              class="elsa-gauge-promise"
              :style="{ left: promiseLeft }"
              data-test="elsa-promise-mark"
            ></span>
            <span class="elsa-gauge-needle" :style="{ left: markerLeft }"></span>
          </div>

          <div class="elsa-gauge-ticks">
            <span style="left: 0">0</span>
            <span :style="{ left: tickLeft(measured.typical_high) }"
              >{{ measured.typical_high }}d</span
            >
            <span :style="{ left: tickLeft(measured.late_from) }">{{ measured.late_from }}d</span>
            <span style="left: 100%">{{ measured.scale_max }}d+</span>
          </div>

          <!--
            The zone names live here rather than inside the bar. Colour alone
            cannot carry a three-way distinction for a reader who does not see
            it, and a word inside a segment distorts the segment.
          -->
          <div class="elsa-gauge-key">
            <span :class="{ 'elsa-gauge-key--on': measured.verdict === 'good' }">
              <i class="elsa-sw elsa-sw--good"></i>Typical
            </span>
            <span :class="{ 'elsa-gauge-key--on': measured.verdict === 'watch' }">
              <i class="elsa-sw elsa-sw--watch"></i>Watch{{
                measured.verdict === 'watch' ? ' — you are here' : ''
              }}
            </span>
            <span :class="{ 'elsa-gauge-key--on': measured.verdict === 'late' }">
              <i class="elsa-sw elsa-sw--late"></i>Late{{
                measured.verdict === 'late' ? ' — you are here' : ''
              }}
            </span>
            <span v-if="promiseLeft" class="elsa-gauge-key--promise">
              <i class="elsa-sw elsa-sw--promise"></i>Promised ship {{ promiseDateText }}
            </span>
          </div>

          <p class="elsa-band-why">
            {{ measured.stage_label }} has taken
            <strong>{{ measured.typical_low }}–{{ measured.typical_high }} days</strong>
            across the last {{ measured.sample_size }} orders at this factory.
            <template v-if="promiseText">
              This one is at <strong>{{ measured.days_in_stage }}</strong
              >, with the promised ship date <strong>{{ promiseText }}</strong
              >.
            </template>
          </p>
        </div>

        <!--
          No band rather than a band over four orders. A percentile that swings
          on one outlier teaches people the measurement is noise, which would
          cost more than the missing comparison.
        -->
        <p v-else class="elsa-band-why elsa-band-why--thin" data-test="elsa-no-band">
          Not enough history at this factory to compare this stage yet
          <template v-if="measured.sample_size > 0">
            ({{ measured.sample_size }} comparable
            {{ measured.sample_size === 1 ? 'order' : 'orders' }})</template
          >.
        </p>
      </template>

      <!--
        The card could not be filled. Without this the header renders over
        nothing and the card reads as broken rather than as unavailable — which
        is exactly what it looked like when the tab shipped ahead of its
        endpoint. A measured card that cannot say anything has to say that.
      -->
      <p v-else class="elsa-band-why elsa-band-why--thin" data-test="elsa-measured-unavailable">
        {{ error || 'These figures could not be loaded right now.' }}
      </p>
    </section>

    <!-- GENERATED -->
    <section class="elsa-card" data-test="elsa-reading">
      <!--
        Freshness is the second thing on this card, not the last.

        It used to be an absolute date in the footer, under everything it was
        meant to qualify — by the time a reader met it they had already believed
        the summary. As an age beside the name, with the count of events behind
        it, it is read before the words it applies to. Regenerate sits on the
        same line for the same reason: the moment of doubt and the remedy are
        one glance apart, rather than a card's height and a support launcher
        away from each other.
      -->
      <header class="elsa-card-head elsa-read-head">
        <h3 class="elsa-card-title">
          <ElsaMark class="elsa-mark" />
          ELSA's read
        </h3>
        <span
          v-if="reading"
          class="elsa-freshness"
          :class="{ 'elsa-freshness--stale': isStale }"
          data-test="elsa-freshness"
        >
          <span class="elsa-dot"></span>
          {{ freshnessText }}
        </span>
        <span v-else class="elsa-card-note">{{ readingNote }}</span>
        <button
          v-if="reading"
          type="button"
          class="elsa-btn elsa-regen"
          :disabled="isGenerating"
          data-test="elsa-regenerate"
          @click="generate"
        >
          <ElsaMark class="elsa-mark" />
          {{ isGenerating ? 'Reading…' : 'Regenerate' }}
        </button>
      </header>

      <!-- Unavailable: the measured card above is unaffected, and says so. -->
      <div v-if="!available" class="elsa-notice" data-test="elsa-unavailable">
        <strong>ELSA is not available right now.</strong>
        The figures above are unaffected — they come from this order's own timeline, not from a
        model.
      </div>

      <div v-else-if="isGenerating" class="elsa-empty">
        <p>Reading this order…</p>
      </div>

      <!--
        Could not even load. Distinct from "never run": offering Generate here
        would be offering a button that cannot work, because the request it
        makes is the one that just failed. Say what happened and stop.
      -->
      <div v-else-if="loadFailed" class="elsa-notice" data-test="elsa-load-failed">
        <strong>This order's analysis could not be loaded.</strong>
        {{ error }}
      </div>

      <!-- Never run for this order. -->
      <div v-else-if="!reading" class="elsa-empty" data-test="elsa-not-run">
        <p>
          ELSA reads this order's timeline, its support threads and its internal comments, and says
          whether it is on track — with the event behind every claim.
        </p>
        <button type="button" class="elsa-btn elsa-btn--primary" @click="generate">
          <ElsaMark class="elsa-mark" />
          Generate analysis
        </button>
        <p v-if="error" class="elsa-error" role="status">{{ error }}</p>
      </div>

      <template v-else>
        <!--
          The verdict as a statement, not a chip.

          A small coloured pill above a paragraph makes the reader do the work
          of joining them. The colour now runs down the left edge of the words
          it qualifies, and the words are set large enough to be the thing that
          gets read.
        -->
        <div class="elsa-verdict" :class="`elsa-verdict--${reading.status}`">
          <p class="elsa-verdict-kicker">{{ statusLabel }}</p>
          <p class="elsa-verdict-text">{{ reading.summary }}</p>
          <p v-if="etaText" class="elsa-eta">
            Ships <strong>{{ etaText }}</strong>
            <span v-if="reading.eta.basis" class="elsa-eta-basis"> — {{ reading.eta.basis }}</span>
          </p>
        </div>

        <!--
          "Do this next", promoted out of grey small print.

          It was the smallest text on the card and ended in a full stop: a
          recommendation that then asked the reader to go and find the right
          button themselves. The buttons open the same modals the action row
          opens, and each is offered only where that row would have offered it.
        -->
        <div v-if="nextAction" class="elsa-cta" data-test="elsa-cta">
          <div class="elsa-cta-body">
            <p class="elsa-cta-kicker">Do this next</p>
            <p class="elsa-cta-text">{{ nextAction.claim }}</p>
            <p v-if="nextAction.evidence" class="elsa-cta-why">{{ nextAction.evidence }}</p>
          </div>
          <div class="elsa-cta-btns">
            <button
              type="button"
              class="elsa-btn elsa-btn--primary"
              data-test="elsa-cta-comment"
              @click="act('internal-comment')"
            >
              Add internal comment
            </button>
            <button
              type="button"
              class="elsa-btn"
              data-test="elsa-cta-threads"
              @click="act('support-threads')"
            >
              Open support thread
            </button>
          </div>
        </div>

        <ElsaClaimGroup title="What is going wrong" kind="risk" :claims="reading.risks" />
        <ElsaClaimGroup title="What is going right" kind="good" :claims="reading.signals" />
        <!--
          The remaining actions only. The first is already above as the CTA, and
          repeating it verbatim two inches lower reads as two recommendations.
        -->
        <ElsaClaimGroup
          v-if="remainingActions.length"
          title="Also worth doing"
          kind="action"
          :claims="remainingActions"
        />

        <!--
          Coverage, not confidence. What the reading was actually based on is a
          fact; how sure the model felt is not.
        -->
        <div v-if="coverageChips.length" class="elsa-coverage">
          <span class="elsa-coverage-label">Read from</span>
          <span v-for="chip in coverageChips" :key="chip.label" class="elsa-chip">
            <strong>{{ chip.count }}</strong> {{ chip.label }}
          </span>
        </div>

        <p v-if="error" class="elsa-error" role="status">{{ error }}</p>
      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch, type ComputedRef } from 'vue';

import ElsaClaimGroup from '@/components/Dashboard/Order/components/ElsaClaimGroup.vue';
import ElsaMark from '@/components/Dashboard/Order/components/ElsaMark.vue';
import { formatDate } from '@/composables';
import { useOrderAnalysis } from '@/composables/useOrderAnalysis';

const props = defineProps<{ orderItemId: number }>();

/**
 * `staleness` drives the dot on the tab, which this component cannot reach.
 * `act` asks the page to open the modal a recommendation is asking for, rather
 * than telling the reader to go and find the button.
 */
const emit = defineEmits<{
  (e: 'staleness', stale: boolean): void;
  (e: 'act', action: 'internal-comment' | 'support-threads'): void;
}>();

// One order per mount. OrderDetail keys this component on the order item, so
// clicking to another order remounts rather than mutating — the one thing this
// tab must never do is show the previous order's reading under the current
// order's number, and a key is a far harder guarantee of that than a watcher.
const analysis = useOrderAnalysis(props.orderItemId);

const measured = computed(() => analysis.measured.value);
const reading = computed(() => analysis.reading.value);
const available = computed(() => analysis.available.value);
const isLoading = computed(() => analysis.isLoading.value);
const isGenerating = computed(() => analysis.isGenerating.value);
const error = computed(() => analysis.error.value);
const isStale = computed(() => analysis.isStale.value);

/**
 * The load itself failed, as opposed to the order simply never having been
 * analysed. The two look identical in state — no reading — and must not look
 * identical on screen: one invites a click, the other has to explain itself.
 */
const loadFailed = computed(
  () => !isLoading.value && measured.value === null && error.value !== null
);

const generate = (): Promise<void> => analysis.generate();

onMounted(() => analysis.load());

// Report staleness upward whenever it changes, including on the first load —
// the tab's dot has no other source for it.
watch(isStale, (stale) => emit('staleness', stale), { immediate: true });

const STATUS_LABELS: Record<string, string> = {
  green: 'On track',
  orange: 'At risk',
  red: 'Delayed',
};

const statusLabel: ComputedRef<string> = computed(
  () => STATUS_LABELS[reading.value?.status ?? ''] ?? 'Unknown'
);

const readingNote: ComputedRef<string> = computed(() => {
  if (!available.value) return 'unavailable';
  if (!reading.value) return 'not run for this order';

  return 'written by ELSA · check before quoting';
});

/**
 * Band geometry. One linear scale from 0 to scale_max carries the segment
 * widths, the marker and the ticks, so a tick always lines up with the segment
 * edge it names — a legend spread evenly across a band whose segments are not
 * even is a chart that lies.
 */
const scaleMax: ComputedRef<number> = computed(() => Math.max(1, measured.value?.scale_max ?? 1));

const segments = computed(() => {
  const high = measured.value?.typical_high ?? 0;
  const late = measured.value?.late_from ?? 0;

  return {
    good: high,
    watch: Math.max(0, late - high),
    late: Math.max(0, scaleMax.value - late),
  };
});

/**
 * A position on the scale, as a percentage.
 *
 * Rounded to four places: 11/20 lands on 55.00000000000001 in binary floating
 * point, and shipping that into a style attribute is both noise in the DOM and
 * a value no assertion can match without knowing about float error. Four places
 * is finer than a pixel on any width this pane takes.
 */
const pct = (value: number): string => {
  const raw = Math.min(100, Math.max(0, (value / scaleMax.value) * 100));

  return `${Math.round(raw * 10000) / 10000}%`;
};

const markerLeft: ComputedRef<string> = computed(() => pct(measured.value?.days_in_stage ?? 0));

const tickLeft = (value: number | null): string => pct(value ?? 0);

/**
 * Where the promised ship date falls on the same axis, or '' for no mark.
 *
 * The backend withholds promise_at_days when the promise sits past the drawn
 * scale, so an empty string here means "do not draw", not "no promise" — the
 * date itself can still be present and still be worth saying in prose.
 */
const promiseLeft: ComputedRef<string> = computed(() => {
  const at = measured.value?.promise_at_days;

  return typeof at === 'number' ? pct(at) : '';
});

/**
 * The promised date, short.
 *
 * Deliberately not formatDate: this sits in a key beneath a chart, next to
 * three other entries, and "15th September 2026" there pushes the key onto a
 * second line and swamps the zone names it is meant to sit alongside. The year
 * is redundant on an order in production, and the long form is still used in
 * the sentence below the chart where there is room for it.
 */
const promiseDateText: ComputedRef<string> = computed(() => {
  const iso = measured.value?.promised_ship_date;
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
});

/**
 * The promise in the reader's terms — "2 days away", "3 days ago", "today".
 *
 * Days, not a date, because the decision this drives is whether to chase the
 * factory now, and nobody makes that decision by subtracting dates in their
 * head while reading a card.
 */
const promiseText: ComputedRef<string> = computed(() => {
  const days = measured.value?.days_until_promise;
  if (typeof days !== 'number') return '';

  const whole = Math.round(Math.abs(days));

  if (whole === 0) return 'today';

  const unit = whole === 1 ? 'day' : 'days';

  return days > 0 ? `${whole} ${unit} away` : `${whole} ${unit} ago`;
});

/**
 * How far past typical this order is, for the headline.
 *
 * Only shown once it IS past typical. Below the line the number needs no
 * qualifier, and "2.5 inside typical" is a statistic nobody asked for.
 */
const gapText: ComputedRef<string> = computed(() => {
  const m = measured.value;
  if (!m?.has_comparison || m.typical_high === null) return '';

  const over = m.days_in_stage - m.typical_high;
  if (over <= 0) return '';

  return `${Math.round(over * 10) / 10} beyond typical`;
});

const bandDescription: ComputedRef<string> = computed(() => {
  const m = measured.value;
  if (!m) return '';

  return `${m.days_in_stage} days in ${m.stage_label}; typical is ${m.typical_low} to ${m.typical_high} days, late from ${m.late_from} days.`;
});

const etaText: ComputedRef<string> = computed(() => {
  const eta = reading.value?.eta;
  if (!eta?.earliest || !eta?.latest) return '';

  // Always a range. A single date gets pasted into a customer email and becomes
  // a commitment the business never made.
  return `${formatDate(eta.earliest)} – ${formatDate(eta.latest)}`;
});

const COVERAGE_LABELS: Record<string, [string, string]> = {
  timeline_events: ['timeline event', 'timeline events'],
  internal_comments: ['internal comment', 'internal comments'],
  support_threads: ['support thread', 'support threads'],
};

const coverageChips = computed(() =>
  Object.entries(reading.value?.coverage ?? {}).map(([key, count]) => {
    const [one, many] = COVERAGE_LABELS[key] ?? [key, key];

    return { count, label: count === 1 ? one : many };
  })
);

/**
 * How old the reading is, in the terms that decide whether to trust it.
 *
 * An age rather than a date, because "12 September" requires the reader to know
 * today's date and to care; "2 days old" does not. The event count is appended
 * when there is one, and that is the part that actually matters — a reading
 * written an hour ago with a stage change behind it is staler than one written
 * last week on an order that has not moved.
 */
const freshnessText: ComputedRef<string> = computed(() => {
  const r = reading.value;
  if (!r) return '';

  const age = r.generated_at ? relativeAge(r.generated_at) : 'just now';

  if (!isStale.value) return `updated ${age}`;

  const events = r.events_since;

  return `${age} · ${events} ${events === 1 ? 'event' : 'events'} since`;
});

/**
 * A coarse, unambiguous age. Deliberately not a library.
 *
 * Rounds down, so a reading never claims to be newer than it is, and stops at
 * days: past a week the exact figure stops changing the decision, and "14 days
 * old" already says everything "2 weeks old" would.
 */
const relativeAge = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'recently';

  const minutes = Math.floor((Date.now() - then) / 60000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);

  return `${days} day${days === 1 ? '' : 's'} old`;
};

/**
 * The single action the card leads with, and the rest.
 *
 * The model returns actions in priority order, so the first is the one to
 * promote. The others keep their group lower down; repeating the first in both
 * places would read as two separate recommendations that happen to match.
 */
const nextAction = computed(() => reading.value?.actions?.[0] ?? null);
const remainingActions = computed(() => (reading.value?.actions ?? []).slice(1));

const act = (action: 'internal-comment' | 'support-threads'): void => emit('act', action);
</script>

<style scoped>
.ask-elsa {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px;

  /*
   * Room for the support launcher.
   *
   * The Zendesk messaging launcher is fixed to the viewport's bottom-right on
   * every page of this portal (AB#21165) and floats above this pane. Without
   * this padding the last row of the tab — which is exactly where Regenerate
   * sits — comes to rest underneath it and cannot be clicked at all.
   *
   * Padding rather than a z-index fight: the launcher SHOULD be on top. It is
   * how someone reports the very problem they are looking at, and burying it
   * under an order pane would be the worse trade.
   *
   * The number is the launcher (~64px) plus its own viewport margin, rounded
   * up. If Zendesk's launcher is ever moved or resized in Admin Center, this is
   * the one place that needs to follow it.
   */
  padding-bottom: 92px;
}

.elsa-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  padding: 16px 18px;
}

.elsa-card-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.elsa-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #16211e;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0;
}

.elsa-card-note {
  font-size: 12px;
  color: #7d8a87;
}

.elsa-mark {
  width: 15px;
  height: 15px;
  color: #7c5cd6;
}

.elsa-skeleton {
  height: 76px;
  border-radius: 6px;
  background: #f3f4f6;
}

.elsa-metric {
  margin: 0;
}

.elsa-metric-value {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: #16211e;
}

/*
 * The headline takes the verdict's colour, so the answer is legible before the
 * eye reaches the bar at all. Ink for "typical" — a number that is fine does
 * not need a colour to say so, and green here would compete with the gauge.
 */
.elsa-metric-value--watch {
  color: #a8690f;
}

.elsa-metric-value--late {
  color: #be2f24;
}

.elsa-metric-unit {
  font-size: 14px;
  font-weight: 500;
  color: #7d8a87;
  margin-left: 4px;
}

.elsa-gauge {
  margin-top: 14px;
}

/*
 * The value flag, on a row of its own.
 *
 * It shared a strip with the zone names before, and at the widths this pane
 * takes on a laptop the two overlapped — the number sat on top of the word.
 */
.elsa-gauge-flagrow {
  position: relative;
  /* Tall enough to clear the needle's cap, which sits 5px proud of the rail. */
  height: 34px;
}

.elsa-gauge-flag {
  position: absolute;
  bottom: 11px;
  transform: translateX(-50%);
  background: #16211e;
  color: #fff;
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.elsa-gauge-flag::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -3px;
  transform: translateX(-50%) rotate(45deg);
  width: 7px;
  height: 7px;
  background: #16211e;
  border-radius: 1px;
}

.elsa-gauge-rail {
  position: relative;
  display: flex;
  height: 12px;
}

/*
 * flex-basis: 0 is load-bearing.
 *
 * Without it flex-grow shares only the space LEFT OVER after every item has
 * taken its content width, so the segments were drawn at the wrong widths and
 * disagreed with the ticks and the marker — which are on the true scale. That
 * is the whole reason a reading inside Watch appeared to sit on the edge of
 * Typical. min-width: 0 stops the same thing happening again by any other
 * route.
 */
.elsa-gauge-zone {
  flex-basis: 0;
  min-width: 0;
}

/*
 * Only the zone the order is actually in is saturated. Three full-strength
 * blocks make a traffic light that shouts before the number has been read; the
 * washes still delimit the scale without competing with the answer.
 */
.elsa-gauge-zone--good {
  background: #cfe8dc;
  border-radius: 4px 0 0 4px;
}

.elsa-gauge-zone--watch {
  background: #f4e2c4;
}

.elsa-gauge-zone--late {
  background: #f6d6d2;
  border-radius: 0 4px 4px 0;
}

.elsa-gauge-zone--watch.elsa-gauge-zone--on {
  background: #b0741a;
}

.elsa-gauge-zone--late.elsa-gauge-zone--on {
  background: #c4362c;
}

/*
 * A real pointer. The 8px triangle it replaces was the weakest mark on a card
 * whose entire job is to show where one number falls.
 */
.elsa-gauge-needle {
  position: absolute;
  top: -6px;
  bottom: -6px;
  width: 3px;
  border-radius: 2px;
  background: #16211e;
  transform: translateX(-50%);
  z-index: 2;
}

.elsa-gauge-needle::after {
  content: '';
  position: absolute;
  left: 50%;
  top: -5px;
  transform: translateX(-50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #16211e;
  box-shadow: 0 0 0 2.5px #fff;
}

/* Dashed, and in ELSA's purple rather than a status colour: the promise is a
   date, not a verdict, and painting it amber would assert one. */
.elsa-gauge-promise {
  position: absolute;
  top: -6px;
  bottom: -6px;
  width: 2px;
  z-index: 1;
  transform: translateX(-50%);
  background: repeating-linear-gradient(180deg, #7c5cd6 0 3px, transparent 3px 6px);
}

.elsa-gauge-ticks {
  position: relative;
  height: 15px;
  margin-top: 10px;
  font-size: 10.5px;
  color: #9aa8a4;
}

.elsa-gauge-ticks span {
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.elsa-gauge-ticks span:first-child {
  transform: none;
}

.elsa-gauge-ticks span:last-child {
  transform: translateX(-100%);
}

/*
 * The key. The zone names used to sit inside the bar, which is both what
 * distorted the geometry and what left the distinction resting on colour alone.
 */
.elsa-gauge-key {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 9px;
  font-size: 11.5px;
  color: #7d8a87;
}

.elsa-gauge-key span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.elsa-gauge-key .elsa-gauge-key--on,
.elsa-gauge-key span.elsa-gauge-key--on {
  color: #16211e;
  font-weight: 700;
}

.elsa-gauge-key--promise {
  color: #5b3fb0;
  font-weight: 600;
}

.elsa-sw {
  width: 12px;
  height: 8px;
  border-radius: 2px;
  flex: none;
}

/*
 * A key swatch shows the colour the bar is actually painted, which means the
 * washes — and the saturated colour only on the zone the order is in. A key
 * that shows three strong colours against a bar showing one is a key for a
 * different chart.
 */
.elsa-sw--good {
  background: #cfe8dc;
}

.elsa-sw--watch {
  background: #f4e2c4;
}

.elsa-sw--late {
  background: #f6d6d2;
}

.elsa-gauge-key--on .elsa-sw--watch {
  background: #b0741a;
}

.elsa-gauge-key--on .elsa-sw--late {
  background: #c4362c;
}

.elsa-sw--promise {
  width: 2px;
  height: 12px;
  border-radius: 0;
  background: repeating-linear-gradient(180deg, #7c5cd6 0 3px, transparent 3px 6px);
}

.elsa-band-why {
  margin: 10px 0 0;
  font-size: 13px;
  color: #4b5855;
}

.elsa-band-why--thin {
  margin-top: 12px;
}

.elsa-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  flex: none;
}

/*
 * The header is now a row of three: name, freshness, Regenerate hard right.
 */
.elsa-read-head {
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.elsa-freshness {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #7d8a87;
  background: #f4f6f5;
  border-radius: 999px;
  padding: 4px 10px;
  white-space: nowrap;
}

/*
 * Amber, and heavier, once there are events behind the reading. The whole
 * reason this is on the card is to stop a confident summary of an order that
 * has since moved being read as current.
 */
.elsa-freshness--stale {
  background: #fbf1e0;
  color: #a8690f;
  font-weight: 600;
}

.elsa-regen {
  margin-left: auto;
}

/* The verdict: colour on the edge of the words it qualifies. */
.elsa-verdict {
  border-left: 3px solid #7d8a87;
  padding-left: 14px;
  margin: 0 0 16px;
}

.elsa-verdict--green {
  border-left-color: #1e8e5a;
}

.elsa-verdict--orange {
  border-left-color: #b0741a;
}

.elsa-verdict--red {
  border-left-color: #c4362c;
}

.elsa-verdict-kicker {
  margin: 0 0 5px;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: #4b5855;
}

.elsa-verdict--green .elsa-verdict-kicker {
  color: #1e8e5a;
}

.elsa-verdict--orange .elsa-verdict-kicker {
  color: #a8690f;
}

.elsa-verdict--red .elsa-verdict-kicker {
  color: #be2f24;
}

.elsa-verdict-text {
  margin: 0;
  font-size: 16.5px;
  line-height: 1.45;
  color: #16211e;
  max-width: 62ch;
}

/*
 * The call to action, given a surface of its own.
 *
 * Purple rather than a status colour: this is ELSA asking for something, and
 * painting it amber would make it look like a second verdict.
 */
.elsa-cta {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
  margin: 0 0 16px;
  padding: 14px 15px;
  border: 1px solid #ddd2fb;
  border-radius: 9px;
  background: #f8f5ff;
}

.elsa-cta-body {
  flex: 1;
  min-width: 220px;
}

.elsa-cta-kicker {
  margin: 0 0 4px;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: #5b3fb0;
}

.elsa-cta-text {
  margin: 0;
  font-size: 14.5px;
  font-weight: 600;
  line-height: 1.4;
  color: #16211e;
}

.elsa-cta-why {
  margin: 5px 0 0;
  font-size: 12px;
  color: #7d8a87;
}

.elsa-cta-btns {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.elsa-cta-btns .elsa-btn {
  white-space: nowrap;
}

.elsa-eta {
  margin: 8px 0 0;
  font-size: 13.5px;
  color: #4b5855;
}

.elsa-eta strong {
  color: #16211e;
  font-weight: 600;
}

.elsa-eta-basis {
  color: #7d8a87;
}

.elsa-coverage {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  align-items: center;
}

.elsa-coverage-label {
  font-size: 12px;
  color: #7d8a87;
}

.elsa-chip {
  font-size: 11px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 3px 9px;
  color: #7d8a87;
  background: #f6f9f8;
}

.elsa-chip strong {
  color: #16211e;
  font-weight: 500;
}

.elsa-btn {
  font-size: 13px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 5px;
  border: 1px solid #c3cdca;
  background: #fff;
  color: #16211e;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.elsa-btn:hover:not(:disabled) {
  background: #f6f9f8;
  border-color: #7d8a87;
}

.elsa-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.elsa-btn--primary {
  background: #219f84;
  border-color: #219f84;
  color: #fff;
}

.elsa-btn--primary:hover:not(:disabled) {
  background: #1b8670;
  border-color: #1b8670;
}

.elsa-btn--primary .elsa-mark {
  color: #e9ddff;
}

.elsa-empty {
  text-align: center;
  padding: 26px 18px;
}

.elsa-empty p {
  color: #4b5855;
  max-width: 46ch;
  margin: 0 auto 14px;
  font-size: 13.5px;
}

.elsa-notice {
  background: #fbf1e0;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #b0741a;
  border-radius: 0 8px 8px 0;
  padding: 13px 16px;
  font-size: 13.5px;
  color: #4b5855;
}

.elsa-notice strong {
  color: #16211e;
}

.elsa-error {
  margin: 10px 0 0;
  font-size: 13px;
  color: #c4362c;
}
</style>
